import { useEffect, useState } from 'react'
import { listMySends, undoSend, type SentItem } from '@core/documents'
import { Button, Card, Section } from '@design/components'
import { DocumentThumb } from '@features/documents/DocumentThumb'
import { DocumentViewer } from '@features/documents/DocumentViewer'

/**
 * The closure trail.
 *
 * This list is the reason a parent keeps using the app instead of going back
 * to WhatsApp: it answers "did my child actually deal with it?" without them
 * having to ask. Keep it boring and truthful.
 */
export function RecentlySent({ refreshKey }: { refreshKey: number }) {
  const [items, setItems] = useState<SentItem[] | null>(null)
  const [viewing, setViewing] = useState<SentItem | null>(null)

  useEffect(() => {
    let alive = true
    const load = () =>
      listMySends()
        .then((rows) => alive && setItems(rows))
        .catch(() => alive && setItems([]))
    void load()
    // Cheap polling instead of realtime: three users, and a wrong "handled"
    // state is worse than a slightly late one.
    const t = setInterval(load, 15000)
    return () => {
      alive = false
      clearInterval(t)
    }
  }, [refreshKey])

  if (!items || items.length === 0) return null

  return (
    <>
      <Section title="Recent verstuurd">
        <div className="space-y-2">
          {items.map((item) => (
            <Row key={item.id} item={item} onOpen={() => !item.pending && setViewing(item)} />
          ))}
        </div>
      </Section>

      {viewing && (
        <DocumentViewer
          documentId={viewing.id}
          title={rowTitle(viewing)}
          onClose={() => setViewing(null)}
        />
      )}
    </>
  )
}

/**
 * What the row is called.
 *
 * Every one of these used to say "Document". Five sends in a week became five
 * identical lines, and "did the electricity bill get paid?" had no answer on
 * the screen that exists to answer exactly that.
 *
 * The supplier is the best name and arrives once the admin verifies. Before
 * then nobody honestly knows what the paper is, so the fallback is the one
 * thing the parent does remember: when they took the photo. "Gisteren 14:22"
 * is not a description, but it is a handle — they were there.
 */
export function rowTitle(item: SentItem): string {
  if (item.title) return item.title
  if (item.pending) return 'Wordt verstuurd'

  const at = new Date(item.createdAt)
  const time = new Intl.DateTimeFormat('nl-BE', { hour: '2-digit', minute: '2-digit' }).format(at)
  const today = new Date()
  const sameDay =
    at.getFullYear() === today.getFullYear() &&
    at.getMonth() === today.getMonth() &&
    at.getDate() === today.getDate()
  if (sameDay) return `Gestuurd om ${time}`

  const day = new Intl.DateTimeFormat('nl-BE', { weekday: 'long', day: 'numeric', month: 'short' })
    .format(at)
  return `${day}, ${time}`
}

function Row({ item, onOpen }: { item: SentItem; onOpen: () => void }) {
  const [undone, setUndone] = useState(false)
  const [canUndo, setCanUndo] = useState(withinUndoWindow(item))

  useEffect(() => {
    if (!canUndo) return
    const t = setInterval(() => setCanUndo(withinUndoWindow(item)), 1000)
    return () => clearInterval(t)
  }, [canUndo, item])

  if (undone) return null

  const closed = item.state === 'paid' || item.state === 'handled'

  return (
    <Card className="flex items-center gap-3">
      {/* The parent took this photo. Showing it back is how they recognise
          their own send without reading a word. */}
      {!item.pending && (
        <DocumentThumb documentId={item.id} onOpen={onOpen} className="shrink-0" />
      )}

      <div className="flex-1 min-w-0">
        <p className="font-medium truncate">{rowTitle(item)}</p>
        <p className={`text-sm ${closed ? 'text-ok' : 'text-muted'}`}>{item.statusLine}</p>
        <p className="text-xs text-muted">{relative(item.createdAt)}</p>
      </div>

      {canUndo && !item.pending && (
        <Button
          tone="quiet"
          className="w-auto shrink-0"
          onClick={async () => {
            try {
              await undoSend(item.id)
              setUndone(true)
            } catch {
              setCanUndo(false)
            }
          }}
        >
          Ongedaan maken
        </Button>
      )}
    </Card>
  )
}

/**
 * Sixty seconds, matching the server-side window in undo_send(). Shown only
 * while it would actually succeed — offering an action that then fails is
 * worse than not offering it.
 */
function withinUndoWindow(item: SentItem): boolean {
  return Date.now() - new Date(item.createdAt).getTime() < 60_000
}

function relative(iso: string): string {
  const seconds = Math.floor((Date.now() - new Date(iso).getTime()) / 1000)
  if (seconds < 60) return 'net'
  const minutes = Math.floor(seconds / 60)
  if (minutes < 60) return `${minutes} min geleden`
  const hours = Math.floor(minutes / 60)
  if (hours < 24) return `${hours} u geleden`
  const days = Math.floor(hours / 24)
  return days === 1 ? 'gisteren' : `${days} dagen geleden`
}
